import { DEFAULT_LANGUAGE_KEY, LOCAL_STORAGE_KEYS } from 'utils/constants'
import { capitalize } from 'utils/misc'
import { fetchLocales, setLocale } from 'store/locales'
import { stringsActions, DEFAULT_LANGUAGE } from 'store/strings'

//
// Parses locale to language parameter used by Applanga
// ex) 'en_US' to 'en'
//
export const parseLocale = locale => {
  if (!locale) return null
  if (locale.includes('_')) {
    return locale.split('_')[0]
  }
  return locale
}

/**
 *
 * @param {*} applangaId Applanga ID that comes from the system's locales table
 * @returns a string of an Applanga key that the Applanga API can read.
 * Standard pattern generally requires the country code to be uppercase for most
 * languages besides Simplified Chinese.
 */
export const formatApplangaKey = applangaId => {
  const [applangaLang, applangaCountry] = applangaId.split('-')
  let modifiedApplanga = applangaId
  if (applangaCountry) {
    /**
     * Applanga keys require the country codes to be uppercase in the API
     * request. In the specific case for Chinese where the dialect/country
     * code is "hans", the dialect portion needs to be sent capitalized,
     * i.e., "zh-Hans". This is why we do a ternary here specific for Chinese.
     *  */

    const modifiedCountry =
      applangaCountry === 'hans'
        ? capitalize(applangaCountry)
        : applangaCountry.toUpperCase()
    modifiedApplanga = `${applangaLang}-${modifiedCountry}`
  }

  return modifiedApplanga
}

const BASELINE_LANG_MAP = {
  de: 'de_DE',
  fr: 'fr_FR',
  pt: 'pt_BR',
  ru: 'ru_RU',
  'zh-hant': 'zh-hant_TW',
  zh: 'zh-hant_TW'
}

/**
 * This helper func takes the fetched locales list and generates a language map
 * that takes the language key with no country code (e.g. "en", "JP")
 * to return a locale key that the system uses (e.g. "en_US", "jp_JP")
 */
const generateLanguageMap = localesList => {
  const langMap = {}
  const memoizedBaselineMap = {}
  localesList.forEach(localeObj => {
    const { id, applanga } = localeObj
    const langKey = id.split('_')[0]
    const modifiedLocale = id.replace('_', '-')
    /**
     * Need to add another set of keys for Safari which uses slightly different
     * casing for the browser detected language, so these will need to be
     * included in the map as well.
     *  */
    const [lang, country] = id.split('_')
    const safariLocale = `${lang}-${country.toLowerCase()}`
    const modifiedApplanga = formatApplangaKey(applanga)

    // langMap[langKey] = { id, applanga: modifiedApplanga }

    if (BASELINE_LANG_MAP[langKey] && id === BASELINE_LANG_MAP[lang]) {
      memoizedBaselineMap[langKey] = { id, applanga: modifiedApplanga }
    }

    if (memoizedBaselineMap[langKey]) {
      langMap[langKey] = memoizedBaselineMap[langKey]
    } else {
      langMap[langKey] = { id, applanga: modifiedApplanga }
    }

    langMap[applanga] = { id, applanga: modifiedApplanga }
    langMap[modifiedApplanga] = { id, applanga: modifiedApplanga }
    langMap[modifiedLocale] = { id, applanga: modifiedApplanga }
    langMap[safariLocale] = { id, applanga: modifiedApplanga }
  })
  return langMap
}

// const generateLanguageKeysFromBrowserDetection = localesList => {
//   // Browser-detected language
//   const detectedLanguage = navigator.language
//   const applangaKey = detectedLanguage.split('-')[0]
//   const langKeyMap = generateLanguageMap(localesList)
//   let languageKey = langKeyMap[applangaKey]

//   /**
//    * We'll default to English if the map which represents our available
//    * languages doesn't have the detected language
//    * */
//   if (!languageKey) languageKey = 'en_US'
//   return [applangaKey, languageKey]
// }

const generateLanguageKeysFromBrowserDetection = localesList => {
  // Browser-detected language
  const detectedLanguage = navigator.language
  // Just the language portion of the detected language without the country
  const langCode = detectedLanguage.split('-')[0]
  const langMap = generateLanguageMap(localesList)

  let languageKey
  let applangaKey

  const detectedLangObj = langMap[detectedLanguage]

  if (detectedLangObj) {
    languageKey = detectedLangObj.id
    applangaKey = detectedLangObj.applanga
  }

  /**
   * We'll default to English if the map which represents our available
   * languages doesn't have the detected language
   * */
  if (!languageKey) languageKey = DEFAULT_LANGUAGE_KEY

  /**
   * If the detected language key is not available on Applanga we will check if
   * just the language key (if there is a country code) is in the
   * dictionary/map, and use just language key for Applanga. If the system
   * doesn't have the detected language in the fetched locales, we fall back
   * to English for the applanga key.
   */

  if (!applangaKey) {
    applangaKey = langMap[langCode]?.applanga || `en`
  }

  return [applangaKey, languageKey]
}

async function fetchLocalesList(fetch) {
  await fetch()
}

/**
 *
 * @param {*} user Participant payload that includes information whether the particpant has an enforced locale
 * @returns An object of the participant enforced language if there is one
 */
export const getCurrentPtpEnforcedLocale = user => {
  const studyConfig = user?.study?.config
  /**
   * We only return a participant-enforced locale if the participant belongs
   * to study where enforced locale is in version 2.
   */
  if (studyConfig?.enforced_locale?.version === 2) {
    return studyConfig.enforced_language?.languages?.[0]
  }
  return null
}

const setLocalStorageLang = val => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.localization, JSON.stringify(val))
}

let localizeRequestId = 0

const getCurrentUser = getState => {
  const stateUser = getState()?.user
  if (stateUser && Object.keys(stateUser).length > 0) {
    return stateUser
  }

  try {
    return JSON.parse(localStorage.getItem('user'))
  } catch (_) {
    return null
  }
}

const localizeApp = ({ dispatch, getState }) => {
  const requestId = ++localizeRequestId

  fetchLocalesList(() => dispatch(fetchLocales())).then(() => {
    if (requestId !== localizeRequestId) return

    const currentUser = getCurrentUser(getState)
    const ptpEnforcedLocale = getCurrentPtpEnforcedLocale(currentUser)
    const localStorageLocale = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.localization)
    )

    let localeId = DEFAULT_LANGUAGE
    let applangaId = DEFAULT_LANGUAGE

    if (localStorageLocale) {
      /**
       * We will first look to see if there is a locally stored language preference that
       * the participant has selected. If there is, we default to that language.
       */
      const { selectedLanguage, applanga } = localStorageLocale
      applangaId = applanga || parseLocale(selectedLanguage)
      localeId = selectedLanguage
    }
    if (ptpEnforcedLocale) {
      const { applanga, id } = ptpEnforcedLocale
      applangaId = formatApplangaKey(applanga)
      setLocalStorageLang({ selectedLanguage: id, applanga: applangaId })
      localeId = id
    } else if (currentUser) {
      const state = getState()
      const { locales } = state

      // Generate relevant keys from language detected from browser with helper
      const [
        applangaKey,
        languageKey
      ] = generateLanguageKeysFromBrowserDetection(locales?.localesList)

      /**
       * If we do not see a selected preferred language that was stored from a previous session,
       * we will default the app to the detected language from the browser.
       */
      applangaId = applangaKey
      localeId = languageKey
      setLocalStorageLang({
        selectedLanguage: languageKey,
        applanga: applangaKey
      })
    }

    dispatch(setLocale(localeId))
    dispatch(stringsActions.onFetchApplangaStrings(applangaId, localeId))
  })
}

export default localizeApp
